import {Component, Mixins, Prop, Watch} from "vue-property-decorator";
import Modal from "@/components/Modal.vue";
import ProductsView from "@/views/products-and-services/ProductsView.vue";
import BekChannelGuide from "@/products-and-services/bek-channel-guide-component/bek-channel-guide/bekChannelGuide.vue";
import SectionSummary from "@/views/products-and-services/components/SectionSummary.vue";
import LoadingMixin from "@/mixins/LoadingMixin";
import IconsMixin from "@/mixins/IconsMixin";
import ProductsServicesMixin from "@/mixins/ProductsServicesMixin";
import ProductMixin from "@/mixins/ProductMixin";
import ThemeMixin from "@/mixins/ThemeMixin";
import {
    BusinessPlanType,
    BusinessType,
    FieldName,
    FireOccupancyTier,
    Service,
    ServiceType,
    ServiceTypeId
} from "@/products-and-services/ProductsAndServicesModels";
import {SectionItem} from "@/views/products-and-services/components/SectionInterfaces";
import DividedTitle from "@/views/products-and-services/components/DividedTitle.vue";
import FinishedButton from "@/views/products-and-services/components/FinishedButton.vue";
import TavernModalWrapper from '@/views/products-and-services/components/TavernModalWrapper.vue';

enum StepEnum {
    BusinessTypeStep = 1,
    BusinessPlanAndOccupancyStep = 2,
    ProductsStep = 3
}

@Component({
    components: {
        BekChannelGuide,
        DividedTitle,
        FinishedButton,
        Modal,
        ProductsView,
        SectionSummary,
        TavernModalWrapper
    },
})
export default class BusinessTvService extends Mixins(LoadingMixin, IconsMixin, ProductsServicesMixin, ProductMixin, ThemeMixin) {
    @Prop({ type: Boolean, required: false })
    private showCompleteView!: boolean;
    @Prop({ type: String, required: false })
    private currentSelectedServiceTypeId!: string;

    private businessType = '';
    private BusinessType = BusinessType;
    private planType: BusinessPlanType = BusinessPlanType.None;
    private PlanType = BusinessPlanType;
    private currentStep = StepEnum.BusinessTypeStep;
    private fireOccupancyTier = '';
    private FireOccupancyTier = FireOccupancyTier;
    private StepEnum = StepEnum;
    private showTavernModal = false;

    private isSaving = false;
    private isLoading = false;

    get isEditing(): boolean {
        return this.currentSelectedServiceTypeId === ServiceTypeId.TV;
    }

    get serviceType(): ServiceType {
        const serviceType = this.$store.getters.getServiceTypeById(ServiceTypeId.TV);
        if (serviceType) {
            return serviceType;
        }

        return {} as ServiceType;
    }

    get selectedService(): Service | undefined {
        const selectedOptions = this.$store.state.shoppingCartData.selectedOptions;
        if (selectedOptions && selectedOptions.services) {
            return selectedOptions.services.find((service: Service) => service.serviceTypeId === this.serviceType.id);
        }
    }

    get availableServices() {
        if (this.$store.state.productIdForCrossSaleCatalogItem && this.serviceType.services) {
            return this.serviceType.services.filter(service =>
                service.categories && service.categories.some(category => category.products.some(product => product.productId === this.$store.state.productIdForCrossSaleCatalogItem)));
        }
        return this.serviceType.services;
    }

    get nextStepText(): string {
        const lastWord = this.currentStep === StepEnum.BusinessTypeStep ? 'Plan' : 'Products'
        return `Step ${this.currentStep + 1} &mdash; ${this.serviceType.name} ${lastWord}`
    }

    get businessTypeSummary(): SectionItem[] {
        return [{ id: '', catId: this.businessType, name: this.businessType, amount: '' }];
    }

    get planTypeSummary(): SectionItem[] {
        const summaries: SectionItem[] = [{ id: '', catId: this.planType, name: this.planTypeDisplay(this.planType), amount: '' }];
        if (this.fireOccupancyTier) {
            summaries.push({ id: this.fireOccupancyTier, catId: this.fireOccupancyTier, name: `OCCUPANCY ${this.fireOccupancyTier}`, amount: '' });
        }
        return summaries;
    }

    get needsFireOccupancy(): boolean {
        return Boolean(this.selectedService) && this.businessType === BusinessType.TAVERN && this.planType === BusinessPlanType.Core;
    }

    get coreService(): Service | undefined {
        if (this.businessType) {
            return this.availableServices!.filter(this.typeFilter).filter(this.isCore).pop();
        }
    }

    get basicService(): Service | undefined {
        if (this.businessType) {
            return this.availableServices!.filter(this.typeFilter).filter(this.isBasic).pop();
        }
    }

    get typeFilter(): (service: Service) => boolean {
        return this.businessType === BusinessType.TAVERN ? this.isTavern : this.isStandard;
    }

    get isNotTavern(): boolean {
        return this.businessType !== BusinessType.TAVERN;
    }

    @Watch('businessType')
    private async businessTypeChanged() {
        if (this.businessType === BusinessType.TAVERN && !this.$store.state.shoppingCartData.isTavern) {
            await this.$store.dispatch('setTavern', { isTavern: true });
            this.showTavernModal = true;
            return;
        }
        this.isSaving = true;
        if (!this.isLoading) {
            await this.removeItem();
            this.planType = BusinessPlanType.None;
            this.fireOccupancyTier = '';
        }
        if (this.businessType) {
            this.currentStep = this.fireOccupancyTier ? StepEnum.ProductsStep : StepEnum.BusinessPlanAndOccupancyStep;
        }
        this.isSaving = false;
    }

    @Watch('planType')
    private async planTypeChanged() {
        if (this.planType) {
            await this.selectPlan();
        }
    }

    @Watch('isEditing')
    private isEditingChanged() {
        // When we're no longer editing, we either need to be on Step 3 to show the products if the user has selected a service, or step 1 so the user can select a service.
        if (!this.isEditing) {
            this.currentStep = this.selectedService ? StepEnum.ProductsStep : StepEnum.BusinessTypeStep;
        }
    }

    @Watch('selectedService')
    @Watch('availableServices')
    private selectedServiceChanged() {
        this.setValues();
    }

    @Watch('fireOccupancyTier')
    private async fireOccupancyTierChanged() {
        if (!this.isLoading && !this.isSaving && this.fireOccupancyTier) {
            try {
                this.isSaving = true;
                await this.saveTvPlan();
                this.currentStep = StepEnum.ProductsStep;
            } finally {
                this.isSaving = false;
            }
        }
    }

    created() {
        this.setValues();
    }

    private planTypeDisplay(planType: BusinessPlanType): string {
        switch (planType) {
            case BusinessPlanType.Basic:
                return 'Business';
            case BusinessPlanType.Core:
                return 'Core';
            case BusinessPlanType.None:
            default:
                return '';
        }
    }

    setValues() {
        if (this.isSaving) {
            return;
        }
        if (this.selectedService) {
            this.isLoading = true;
            this.businessType = this.$store.getters.isTavernTvInCart ? BusinessType.TAVERN : BusinessType.STANDARD;
            this.planType = this.isCore(this.selectedService) ? BusinessPlanType.Core : BusinessPlanType.Basic;
            this.setFireOccupancy();
            this.currentStep = StepEnum.ProductsStep;
            /* If we don't wait a moment before setting this,
               businessTypeChanged will remove the selection on refresh because
               the event loop won't pick up the Watch event until this method is finished */
            this.$nextTick(() => this.isLoading = false);
        } else {
            this.currentStep = StepEnum.BusinessTypeStep;
            this.businessType = '';
            this.planType = BusinessPlanType.None;
            this.fireOccupancyTier = '';
        }
    }

    private setFireOccupancy() {
        if (this.needsFireOccupancy) {
            const [val] = this.jsonParseFromValue(this.selectedService!.value);
            this.fireOccupancyTier = val ? val.value : '';
        } else {
            this.fireOccupancyTier = '';
        }
    }

    private isTavern(service: Service): boolean {
        const availableService = this.getMatchingAvailableService(service);
        if (availableService && availableService.tvInfo) {
            return availableService.tvInfo.businessType === BusinessType.TAVERN;
        }
        return false;
    }

    private isStandard(service: Service): boolean {
        return !this.isTavern(service);
    }

    private isBasic(service: Service) {
        return !this.isCore(service);
    }

    private getMatchingAvailableService(service: Service): Service | undefined {
        if (this.availableServices) {
            return this.availableServices.find(aService => aService.serviceId === service.serviceId &&
                    (aService.serviceCatId === service.serviceCatId || aService.catId === service.catId));
        }
    }

    private isCore(service: Service): boolean {
        const availableService = this.getMatchingAvailableService(service);
        if (availableService && availableService.tvInfo) {
            return availableService.tvInfo.businessPlanType === BusinessPlanType.Core;
        }
        return false;
    }

    private async selectPlan() {
        try {
            this.isSaving = true;

            const planFilter = this.planType === BusinessPlanType.Core ? this.isCore : this.isBasic;

            const selectedPlan = this.availableServices!.filter(this.typeFilter).filter(planFilter).pop();

            if (!selectedPlan) {
                return;
            }

            if (this.selectedService && selectedPlan.catId === this.selectedService.catId) {
                return;
            }

            const isNotTavernCore = !this.isTavern(selectedPlan) || !this.isCore(selectedPlan);

            if (isNotTavernCore) {
                this.fireOccupancyTier = '';
            }

            await this.saveTvPlan(selectedPlan);

            if (isNotTavernCore) {
                /* If the user has selected a plan other than tavern core, we move on to Products.
                 * For Tavern Core we don't move to Products because the user has to select Fire Occupancy.
                 */
                this.currentStep = StepEnum.ProductsStep;
            }
        } finally {
            this.isSaving = false;
        }
    }

    private async saveTvPlan(newPlan?: Service) {
        newPlan = newPlan || this.selectedService;

        await this.removeItem();

        const value = this.fireOccupancyTier
            ? this.jsonStringifyValue([{ field: FieldName.FIRE_OCCUPANCY_TIER, value: this.fireOccupancyTier }])
            : '';

        await this.addToCart({
            serviceTypeId: ServiceTypeId.TV,
            serviceId: newPlan!.serviceId,
            serviceCatId: newPlan!.catId,
            value: value,
        });
    }

    private async removeItem() {
        if (this.selectedService) {
            const item = { ...this.getSelectedProduct({} as any, this.selectedService), noRefetch: true };
            await this.removeFromCart(item);
        }
    }

    private editProductClicked() {
        this.currentStep = StepEnum.ProductsStep;
        this.$emit('edit-product', ServiceTypeId.TV);
    }

    private productsComplete() {
        this.$emit('products-complete');
    }

    private editTvService(step: StepEnum) {
        this.currentStep = step;
        this.$emit('edit-clicked');
    }

    private finishedClicked(step: StepEnum) {
        this.currentStep = step;
    }
}
